import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, FileText, Clock, CheckCircle, XCircle } from 'lucide-react';
import { DraftDetailView } from './DraftDetailView';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';

interface Draft {
  id: string;
  title: string;
  region: string;
  status: string;
  content: string;
  source_articles: any[];
  event_clusters: any[];
  composite_score: number;
  created_at: string;
  published_at: string | null;
  rejection_reason: string | null;
  generation_metadata: any;
}

export const DraftReviewList: React.FC = () => {
  const [selectedDraft, setSelectedDraft] = useState<Draft | null>(null);

  const { data: drafts, isLoading, refetch } = useQuery({
    queryKey: ['ai-article-drafts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ai_article_drafts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Draft[];
    },
  });

  const draftsByStatus = {
    draft: drafts?.filter(d => d.status === 'draft') || [],
    approved: drafts?.filter(d => d.status === 'approved') || [],
    published: drafts?.filter(d => d.status === 'published') || [],
    rejected: drafts?.filter(d => d.status === 'rejected') || [],
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft':
        return <Badge variant="secondary" className="gap-1"><Clock className="w-3 h-3" />Venter</Badge>;
      case 'approved':
        return <Badge variant="default" className="gap-1"><CheckCircle className="w-3 h-3" />Godkjent</Badge>;
      case 'published':
        return <Badge variant="default" className="gap-1 bg-green-600"><CheckCircle className="w-3 h-3" />Publisert</Badge>;
      case 'rejected':
        return <Badge variant="destructive" className="gap-1"><XCircle className="w-3 h-3" />Avvist</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getRegionDisplayName = (region: string) => {
    const mapping: Record<string, string> = {
      europa: 'Europa',
      nord_amerika: 'Nord-Amerika',
      asia: 'Asia',
      latin_amerika: 'Latin-Amerika',
      midtosten: 'Midtøsten',
      afrika: 'Afrika',
      skandinavia: 'Skandinavia',
    };
    return mapping[region] || region;
  };

  const renderDraftCard = (draft: Draft) => (
    <Card
      key={draft.id}
      className="cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => setSelectedDraft(draft)}
    >
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-muted-foreground" />
              {draft.title}
            </CardTitle>
            <CardDescription className="mt-2">
              {getRegionDisplayName(draft.region)} • {format(new Date(draft.created_at), 'PPP HH:mm', { locale: nb })}
            </CardDescription>
          </div>
          {getStatusBadge(draft.status)}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 text-sm text-muted-foreground">
          <div className="flex items-center justify-between">
            <span>Kilder: {draft.source_articles?.length || 0}</span>
            <span>Clusters: {draft.event_clusters?.length || 0}</span>
            <span>Score: {draft.composite_score.toFixed(2)}</span>
          </div>
          {draft.status === 'rejected' && draft.rejection_reason && (
            <div className="mt-2 p-2 bg-destructive/10 rounded text-destructive text-xs">
              <strong>Avvisningsgrunn:</strong> {draft.rejection_reason}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (selectedDraft) {
    return (
      <DraftDetailView
        draft={selectedDraft}
        onClose={() => setSelectedDraft(null)}
        onUpdate={() => {
          setSelectedDraft(null);
          refetch();
        }}
      />
    );
  }

  return (
    <Tabs defaultValue="draft" className="w-full">
      <TabsList className="grid w-full grid-cols-4">
        <TabsTrigger value="draft" className="gap-2">
          <Clock className="w-4 h-4" />
          Venter ({draftsByStatus.draft.length})
        </TabsTrigger>
        <TabsTrigger value="approved" className="gap-2">
          <CheckCircle className="w-4 h-4" />
          Godkjent ({draftsByStatus.approved.length})
        </TabsTrigger>
        <TabsTrigger value="published" className="gap-2">
          <CheckCircle className="w-4 h-4" />
          Publisert ({draftsByStatus.published.length})
        </TabsTrigger>
        <TabsTrigger value="rejected" className="gap-2">
          <XCircle className="w-4 h-4" />
          Avvist ({draftsByStatus.rejected.length})
        </TabsTrigger>
      </TabsList>

      <TabsContent value="draft" className="space-y-4 mt-6">
        {draftsByStatus.draft.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Clock className="w-12 h-12 mb-4 opacity-50" />
              <p>Ingen artikler venter på godkjenning</p>
            </CardContent>
          </Card>
        ) : (
          draftsByStatus.draft.map(renderDraftCard)
        )}
      </TabsContent>

      <TabsContent value="approved" className="space-y-4 mt-6">
        {draftsByStatus.approved.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <CheckCircle className="w-12 h-12 mb-4 opacity-50" />
              <p>Ingen godkjente artikler</p>
            </CardContent>
          </Card>
        ) : (
          draftsByStatus.approved.map(renderDraftCard)
        )}
      </TabsContent>

      <TabsContent value="published" className="space-y-4 mt-6">
        {draftsByStatus.published.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <FileText className="w-12 h-12 mb-4 opacity-50" />
              <p>Ingen publiserte artikler</p>
            </CardContent>
          </Card>
        ) : (
          draftsByStatus.published.map(renderDraftCard)
        )}
      </TabsContent>

      <TabsContent value="rejected" className="space-y-4 mt-6">
        {draftsByStatus.rejected.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <XCircle className="w-12 h-12 mb-4 opacity-50" />
              <p>Ingen avviste artikler</p>
            </CardContent>
          </Card>
        ) : (
          draftsByStatus.rejected.map(renderDraftCard)
        )}
      </TabsContent>
    </Tabs>
  );
};
